import type { SearchSection } from '@setplex/tria-api'
import type { UiActionParameters } from '../../ui/parameters/ui-action'

import { createEvent, sample } from 'effector'

/** Content types reported by ET-1.2 (Analytics Keys v2.5). */
export type SearchResultContentType =
  | 'tv'
  | 'vod'
  | 'tvshow'
  | 'catchup'
  | 'event'

export type SearchResultClickPayload = {
  section: SearchSection
  /** Zero-based position of the card within its own section (ET-1.15) */
  position: number
  /** Number of results the section returned (ET-1.14) */
  results: number
  contentType?: SearchResultContentType
  /** Platform content only (ET-1.1) */
  contentId?: number | string
  /** Title of the result: channel, movie, TV show or event name (ET-1.4) */
  contentName?: string
  /** External/MOTN content only, which has no id at all */
  externalContentName?: string
  /**
   * ET-1.5, which each content type fills its own way: the programme title for
   * a Watch Now or Recently Aired card, the start time for a live event.
   */
  programTitle?: string
}

/**
 * Search sections reach analytics as a `content_open_from` breadcrumb, not as a
 * bare section key: `Search/WATCH_NOW`, `Search/AVAILABLE`, … (ET-1.6, ET-2.6).
 */
export const SEARCH_SECTION_ROUTE_PREFIX = 'Search'

export function toSearchSectionRoute(section: SearchSection): string {
  return `${SEARCH_SECTION_ROUTE_PREFIX}/${section}`
}

/** A subscriber selected a search result. */
export const searchResultClicked = createEvent<SearchResultClickPayload>()

export const searchResultClickTracked = createEvent<UiActionParameters>()

sample({
  clock: searchResultClicked,
  fn: ({
    section,
    position,
    results,
    contentType,
    contentId,
    contentName,
    externalContentName,
    programTitle,
  }): UiActionParameters => {
    const external = externalContentName != null

    return {
      // an external result is a Movie of the Night poster, everything else is an
      // ordinary content card (Analytics Keys v2.5, search result click scenarios)
      featureIdentifier: external ? 'movie_of_the_night' : 'content_actions',
      uiElementName: external ? 'movie_of_the_night_poster' : 'content_card',
      route: toSearchSectionRoute(section),
      // AC-4: only platform content is identified by an id — an external result
      // has none, so its name is its whole identity
      contentId: contentId == null ? undefined : String(contentId),
      contentName: externalContentName ?? contentName,
      contentType,
      programName: programTitle,
      eventParameter14: String(results),
      eventParameter15: String(position),
    }
  },
  target: searchResultClickTracked,
})
